/* eslint-disable react/jsx-props-no-spreading */
import _ from 'lodash';
import { FAQ_META_DESCRIPTION, FAQ_META_TITLE, PATH_FAQ } from 'constants';
import { useRouter } from 'next/router';
import { useTheme } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import HeroLayout from 'layouts/HeroLayout';
import Grid from '@mui/material/Grid';
import makeClasses from 'hooks/useClasses';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import PageHead from 'components/Head/PageHead';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import RichTextRenderer from 'components/Text/RichTextRenderer';
import styles from 'styles/jss/pages/faqStyle';
import Typography from '@mui/material/Typography';

import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

const TAG_ICONS = {
  shipping: ({ ...props }) => <LocalShippingOutlinedIcon {...props} />,
  orders: ({ ...props }) => <RoomServiceOutlinedIcon {...props} />,
  'creating-scribble-books': ({ ...props }) => <AutoStoriesOutlinedIcon {...props} />,
  'wrapping-paper': ({ ...props }) => <Inventory2OutlinedIcon {...props} />,
  'gift-cards': ({ ...props }) => <CardGiftcardIcon {...props} />
};

const FAQS = [
  'creating-scribble-books',
  'orders',
  'wrapping-paper',
  'shipping',
  'gift-cards'
];

const FaqHome = ({
  faqs, metaTitle, metaDescription, currentFaq
}) => {
  const accordionRef = useRef();
  const classes = makeClasses(styles);
  const [currentTag, setCurrentTag] = useState();
  const router = useRouter();
  const theme = useTheme();

  useEffect(() => {
    if (accordionRef.current && 'faqId' in router.query) {
      // setCurrentFaq(router.query.faqId);
      const anchor = document.getElementById(`faq_${router.query.faqId}`);
      if (anchor) {
        anchor.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
    if ('faqTag' in router.query) {
      setCurrentTag(router.query.faqTag);
    }
  }, [accordionRef.current]);

  // Preferring crawlable links vs onClick
  // const handleClick = (faq) => {
  //   if (currentFaq === faq.entryId) {
  //     router.push('/faq/');
  //     setCurrentTag(null);
  //     return setCurrentFaq(null);
  //   }
  //   router.push(faq.url, null, {
  //     shallow: true
  //   });
  //   return setCurrentFaq(faq.entryId);
  // };

  const handleTagClick = (tag) => {
    setCurrentTag(tag);

    const anchor = document.getElementById(`header_${tag}`);
    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    router.push(PATH_FAQ, null, {
      shallow: true
    });
  };

  return (
    <>
      <PageHead title={metaTitle} description={metaDescription} />
      <Grid
        container
        justifyContent="start"
        alignContent="start"
        margin="auto"
        sx={{
          pt: 6
        }}
        spacing={{
          xs: 0,
          sm: 6
        }}
      >
        <Grid item xs={12} md={4} lg={3} spacing={4}>
          <Card className={classes.faqNav}>
            <MenuList>
              {FAQS.map((faqTag) => {
                const { tag } = faqs[faqTag];
                const TagIcon = TAG_ICONS[tag.slug];
                return (
                  <MenuItem
                    key={faqTag.tag}
                    sx={{
                      py: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'transparent',
                        color: theme.palette.primary.main,
                        fontWeight: 'bold'
                      }
                    }}
                    selected={tag.slug === currentTag}
                    onClick={() => handleTagClick(tag.slug)}
                    classes={{ selected: classes.selected }}
                  >
                    <ListItemIcon color="primary">
                      <TagIcon
                        fontSize="medium"
                        color={tag.slug === currentTag ? 'primary' : 'grey'}
                      />
                    </ListItemIcon>
                    <ListItemText
                      color="grey"
                      primaryTypographyProps={{
                        paddingLeft: 1,
                        fontWeight: 'bold',
                        color:
                          tag.slug === currentTag
                            ? 'primary'
                            : theme.palette.grey.main
                      }}
                    >
                      {tag.tag}
                    </ListItemText>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Card>
        </Grid>
        <Grid item xs={12} md={8} lg={9} spacing={4}>
          {FAQS.map((faqTag) => {
            const tagLabel = _.get(faqs, [faqTag, 'tag', 'tag']);
            const tagSlug = _.get(faqs, [faqTag, 'tag', 'slug']);
            const faqItems = faqs[faqTag].faqs.map((faq) => {
              const isActive = currentFaq && currentFaq.entryId === faq.entryId;
              return (
                <Accordion
                  ref={accordionRef}
                  // id={`faq_${faq.entryId}`}
                  className={classes.faqAccordion}
                  key={faq.entryId}
                  expanded={isActive}
                  // onChange={() => handleClick(faq)}
                  // https://mui.com/material-ui/react-accordion/#performance
                  TransitionProps={{ unmountOnExit: true }}
                >
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`faq_${faq.entryId}`}
                    id={`faq_id_${faq.entryId}`}
                    component="a"
                    href={faq.url}
                  >
                    <Typography
                      variant={isActive ? 'h1' : 'h3'}
                      sx={{
                        fontSize: '1.2rem',
                        margin: 0
                      }}
                      color={isActive ? 'primary' : 'notBlack'}
                    >
                      {faq.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      <RichTextRenderer data={faq.body} />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            });

            return (
              <Box sx={{ mb: 10 }}>
                <Typography
                  id={`header_${tagSlug}`}
                  className={classes.faqSectionHeader}
                  key={`faqCat_${faqTag}`}
                  variant="h2"
                  color="primary"
                >
                  {tagLabel}
                </Typography>
                {faqItems}
              </Box>
            );
          })}
        </Grid>
      </Grid>
    </>
  );
};

FaqHome.getLayout = (page) => {
  const classes = makeClasses(styles);

  return (
    <HeroLayout
      heroImage="/img/marketing/faq_hero.jpg"
      heroImageTitle="A dad helping his kid hang a picture frame"
      heroStyle={{
        minHeight: '30vh'
      }}
      heroCtaStyle={{
        backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.1), rgba(0,0,0,0.6))'
      }}
      cta={(
        <Box className={classes.homeCta}>
          <Typography
            gutterBottom
            variant="pageTitle"
            color="white"
            component="div"
            className="blogTitle"
          >
            How Can We Help?
          </Typography>
        </Box>
      )}
    >
      {page}
    </HeroLayout>
  );
};

FaqHome.defaultProps = {
  currentFaq: null,
  metaTitle: FAQ_META_TITLE,
  metaDescription: FAQ_META_DESCRIPTION
};

FaqHome.propTypes = {
  faqs: PropTypes.shape().isRequired,
  currentFaq: PropTypes.shape(),
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string
};

export default FaqHome;
